<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <button
          v-if="isWixtarClient"
          class="btn btn-secondary"
          :disabled="syncing"
          @click="handleSyncWixtar"
        >
          <i :class="syncing ? 'ri-loader-4-line spinning' : 'ri-refresh-line'"></i>
          {{ syncing ? '同步中...' : '同步 Wixtar 門市' }}
        </button>
        <button class="btn btn-primary" @click="handleCreate">
                <i class="ri-add-line"></i>
                新增門市
              </button>
      </div>
    </div>

    <div class="page-container">
    <div class="page-toolbar">
      <div class="search-box">
        <input
          v-model="searchQuery"
          type="text"
          class="form-control"
          placeholder="搜尋門市代碼、名稱、地址..."
          @input="handleSearch"
        />
        <i class="ri-search-line"></i>
      </div>
      <div class="filter-group">
        <select v-model="filterStatus" class="form-control" @change="handleFilterChange">
          <option value="">全部狀態</option>
          <option value="active">啟用</option>
          <option value="inactive">停用</option>
          <option value="closed">已關閉</option>
        </select>
      </div>
      <div class="filter-group">
        <select v-model="filterCity" class="form-control" @change="handleFilterChange">
          <option value="">全部城市</option>
          <option v-for="city in cities" :key="city" :value="city">
            {{ city }}
          </option>
        </select>
      </div>
      <div v-if="isWixtarClient" class="filter-group">
        <select v-model="filterDataSource" class="form-control" @change="handleFilterChange">
          <option value="">全部來源</option>
          <option value="wixtar">Wixtar 同步</option>
          <option value="crm">自行新增</option>
        </select>
      </div>
    </div>

    <div v-if="isWixtarClient" class="sync-hint">
      <i class="ri-information-line"></i>
      Wixtar 同步的門市，名稱與地址由 Wixtar 後台管理；自行新增的門市不會被同步覆蓋，也不會回傳至 Wixtar。
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchStores">重試</button>
    </div>

    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>門市代碼</th>
            <th>門市名稱</th>
            <th>地址</th>
            <th>電話</th>
            <th>Email</th>
            <th v-if="isWixtarClient">來源</th>
            <th>狀態</th>
            <th>建立時間</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="stores.length === 0">
            <td :colspan="isWixtarClient ? 9 : 8" class="empty-state">
              沒有找到門市
            </td>
          </tr>
          <tr v-for="store in stores" :key="store.id">
            <td>{{ store.storeCode || '-' }}</td>
            <td>{{ store.storeName || '-' }}</td>
            <td>
              <span v-if="store.city || store.district || store.street">
                {{ [store.city, store.district, store.street].filter(Boolean).join('') }}
              </span>
              <span v-else>-</span>
            </td>
            <td>{{ store.phone || '-' }}</td>
            <td>{{ store.email || '-' }}</td>
            <td v-if="isWixtarClient">
              <span v-if="store.dataSource === 'wixtar'" class="badge badge-info" :title="formatSyncedAt(store.wixtarSyncedAt)">
                Wixtar
              </span>
              <span v-else class="badge badge-secondary">自行新增</span>
            </td>
            <td>
              <span :class="getStatusBadgeClass(store.status)">
                {{ getStatusText(store.status) }}
              </span>
            </td>
            <td>{{ formatDate(store.createdAt) }}</td>
            <td>
              <div class="action-buttons">
                <button
                  class="btn btn-sm btn-secondary"
                  @click="handleView(store)"
                  title="查看詳情"
                >
                  <i class="ri-eye-line"></i>
                </button>
                <button
                  class="btn btn-sm btn-secondary"
                  @click="handleEdit(store)"
                  title="編輯"
                >
                  <i class="ri-edit-line"></i>
                </button>
                <button
                  class="btn btn-sm btn-danger"
                  :disabled="store.dataSource === 'wixtar'"
                  :title="store.dataSource === 'wixtar' ? 'Wixtar 同步的門市無法刪除，請改為停用' : '刪除'"
                  @click="handleDelete(store)"
                >
                  <i class="ri-delete-bin-line"></i>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <ListPagination
      v-if="total > 0"
      :page="currentPage"
      :total-pages="totalPages"
      :total="total"
      @change="goToPage"
    />

    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { storeService } from '@/api/modules/stores.js';
import { clientService } from '@/api/modules/clients.js';
import { wixtarService } from '@/api/modules/wixtar.js';
import authService from '@/helpers/authservice/authService.js';
import { showSuccess, showError, showDeleteConfirm } from '@/utils/swal.js';

export default {
  name: 'ClientStores',
  setup() {
    const router = useRouter();
    const { pageTitle } = usePageTitle('門市管理');
    const stores = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const searchQuery = ref('');
    const filterStatus = ref('');
    const filterCity = ref('');
    const filterDataSource = ref('');
    const cities = ref([]);
    const isWixtarClient = ref(false);
    const syncing = ref(false);
    const currentPage = ref(1);
    const pageSize = ref(20);
    const total = ref(0);
    const searchTimeout = ref(null);

    const totalPages = computed(() => {
      return Math.ceil(total.value / pageSize.value);
    });

    const fetchStores = async () => {
      loading.value = true;
      error.value = null;

      try {
        const params = {
          page: currentPage.value,
          pageSize: pageSize.value,
        };

        if (searchQuery.value) {
          params.search = searchQuery.value;
        }

        if (filterStatus.value) {
          params.status = filterStatus.value;
        }

        if (filterCity.value) {
          params.city = filterCity.value;
        }

        if (filterDataSource.value) {
          params.dataSource = filterDataSource.value;
        }

        const response = await storeService.getStores(params);

        if (response && response.success && response.data) {
          stores.value = response.data.items || [];
          total.value = response.data.pagination?.totalItems || 0;

          // 收集城市列表
          const citySet = new Set();
          stores.value.forEach(store => {
            if (store.city) {
              citySet.add(store.city);
            }
          });
          cities.value = Array.from(citySet).sort();
        } else {
          error.value = response && response.message || '獲取門市列表失敗';
        }
      } catch (err) {
        console.error('獲取門市列表失敗:', err);
        error.value = err.message || '獲取門市列表失敗';
      } finally {
        loading.value = false;
      }
    };

    const loadClientMemberSystem = async () => {
      const user = authService.getCurrentUser();
      if (!user?.clientId) return;
      try {
        const res = await clientService.getClient(user.clientId);
        isWixtarClient.value = res?.data?.clientData?.memberSystemType === 'WIXTAR';
      } catch (err) {
        console.error('載入客戶會員系統類型失敗:', err);
        isWixtarClient.value = false;
      }
    };

    const handleSyncWixtar = async () => {
      syncing.value = true;
      try {
        const response = await wixtarService.syncStores();

        if (response && response.success) {
          const stats = response.data || {};

          if (stats.skipped) {
            await showError('無法同步', stats.message || '此客戶非 Wixtar 會員體系');
            return;
          }

          const details = [
            `Wixtar 回傳 ${stats.rawCount ?? 0} 筆門市`,
            `新增 ${stats.created ?? 0} 筆`,
            `更新 ${stats.updated ?? 0} 筆`
          ];
          if (stats.claimed) {
            details.push(`轉為 Wixtar 管理 ${stats.claimed} 筆`);
          }
          if (stats.deactivated) {
            details.push(`Wixtar 已移除並停用 ${stats.deactivated} 筆`);
          }

          await showSuccess('同步完成', details.join('，'));
          await fetchStores();
        } else {
          await showError('同步失敗', (response && response.message) || '同步 Wixtar 門市失敗');
        }
      } catch (err) {
        console.error('同步 Wixtar 門市失敗:', err);
        await showError('同步失敗', err.message || '同步 Wixtar 門市失敗，請確認 Wixtar 整合設定');
      } finally {
        syncing.value = false;
      }
    };

    const handleSearch = () => {
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
      }
      searchTimeout.value = setTimeout(() => {
        currentPage.value = 1;
        fetchStores();
      }, 500);
    };

    const handleFilterChange = () => {
      currentPage.value = 1;
      fetchStores();
    };

    const goToPage = (page) => {
      if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page;
        fetchStores();
      }
    };

    const handleCreate = () => {
      router.push('/client/stores/create');
    };

    const handleView = (store) => {
      router.push(`/client/stores/${store.id}`);
    };

    const handleEdit = (store) => {
      router.push(`/client/stores/${store.id}/edit`);
    };

    const handleDelete = async (store) => {
      if (store.dataSource === 'wixtar') {
        await showError('無法刪除', 'Wixtar 同步的門市無法刪除，請改為停用，或於 Wixtar 後台移除後重新同步');
        return;
      }

      const confirmed = await showDeleteConfirm(store.storeName || store.storeCode);
      
      if (!confirmed) {
        return;
      }

      const originalLoading = loading.value;
      loading.value = true;
      error.value = null;

      try {
        const response = await storeService.deleteStore(store.id);

        if (response && response.success) {
          await showSuccess('刪除成功', `門市「${store.storeName || store.storeCode}」已成功刪除`);
          
          if (stores.value.length === 1 && currentPage.value > 1) {
            currentPage.value = currentPage.value - 1;
          }
          
          await fetchStores();
        } else {
          const errorMsg = (response && response.message) || '刪除門市失敗';
          await showError('刪除失敗', errorMsg);
        }
      } catch (err) {
        console.error('刪除門市失敗:', err);
        
        let errorTitle = '刪除失敗';
        let errorText = '刪除門市失敗，請稍後再試';
        
        if (err.code === 'NOT_FOUND') {
          errorText = '門市不存在或已被刪除';
        } else if (err.code === 'FORBIDDEN') {
          errorText = '您沒有權限刪除此門市';
        } else if (err.message) {
          errorText = err.message;
        }
        
        await showError(errorTitle, errorText);
      } finally {
        loading.value = originalLoading;
      }
    };

    const formatDate = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    };

    const getStatusBadgeClass = (status) => {
      const statusMap = {
        active: 'badge badge-success',
        inactive: 'badge badge-danger',
        closed: 'badge badge-secondary',
      };
      return statusMap[status] || 'badge badge-secondary';
    };

    const getStatusText = (status) => {
      const statusMap = {
        active: '啟用',
        inactive: '停用',
        closed: '已關閉',
      };
      return statusMap[status] || status;
    };

    const formatSyncedAt = (dateString) => {
      if (!dateString) return '尚未同步';
      return `最後同步：${formatDate(dateString)}`;
    };

    onMounted(() => {
      fetchStores();
      loadClientMemberSystem();
    });

    return {
      stores,
      loading,
      error,
      searchQuery,
      filterStatus,
      filterCity,
      filterDataSource,
      cities,
      isWixtarClient,
      syncing,
      currentPage,
      totalPages,
      total,
      pageTitle,
      handleSearch,
      handleFilterChange,
      goToPage,
      handleCreate,
      handleView,
      handleEdit,
      handleDelete,
      handleSyncWixtar,
      formatDate,
      formatSyncedAt,
      getStatusBadgeClass,
      getStatusText,
      fetchStores,
    };
  },
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.table-wrapper table {
  min-width: 1200px;
}

/* Bootstrap 5 未提供 badge-info / badge-secondary 色階，於此補上來源標記樣式 */
.badge-info {
  background: #d1ecf1;
  color: #0c5460;
}

.badge-secondary {
  background: #e9ecef;
  color: #495057;
}

.sync-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 16px;
  padding: 10px 12px;
  border: 1px solid #c9d8f5;
  border-radius: 6px;
  background: #f8faff;
  font-size: 13px;
  color: #38507a;
}

.spinning {
  display: inline-block;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
